import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Column,
  Grid,
  Modal,
  SIZE,
  Stack,
  Text,
  TextInput,
} from '@quinyx/ui';

import { DELETE_CONFIRM_WORD, EMPTY_VALUE } from '@/constants/support-task';
import { ISupportTask } from '../../api/interfaces/support/task';
import { useTranslate } from '../../hooks/translate';
import { CodeValue, TaskField } from './TaskField';
import { humanDate } from './deleteTaskHelpers';

type ConfirmDeleteTaskModalProps = {
  open: boolean;
  task?: ISupportTask;
  customerName?: string;
  domainId: string;
  ticket: string;
  reason: string;
  isDeleting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
};

const ConfirmDeleteTaskModal = ({
  open,
  task,
  customerName,
  domainId,
  ticket,
  reason,
  isDeleting,
  onOpenChange,
  onConfirm,
}: ConfirmDeleteTaskModalProps) => {
  const { t } = useTranslate();
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmText('');
    }
  }, [open]);

  const {
    _id: taskId,
    name,
    distributedTotal,
    isScheduled,
    scheduledFor,
  } = task || {};
  const isConfirmed = confirmText === DELETE_CONFIRM_WORD;

  return (
    <Modal open={open} onOpenChange={onOpenChange} size="xl">
      <Modal.Header>
        <Modal.CloseButton
          ariaLabel="close-delete-task-modal"
          dataTestId="close-delete-task-modal"
        />
      </Modal.Header>
      <Modal.Content>
        <Modal.HeadlineText>
          {t('support.deleteTask.confirmTitle')}
        </Modal.HeadlineText>
        <Stack spacing={SIZE.M} mt={SIZE.S}>
          <Alert type="negative">
            <Stack spacing={SIZE.XS}>
              <Text variant="smallHeavy">
                {t('support.deleteTask.cannotBeUndone')}
              </Text>
              <Text variant="smallRegular">
                {t('support.deleteTask.confirmBody')}
              </Text>
            </Stack>
          </Alert>
          <Box border borderRadius="l" p={SIZE.M}>
            <Grid columns={[1, 2]} gap={SIZE.M}>
              <TaskField label={t('support.deleteTask.task')}>
                {name || EMPTY_VALUE}
              </TaskField>
              <TaskField label={t('support.deleteTask.taskId')}>
                <CodeValue>{taskId}</CodeValue>
              </TaskField>
              <TaskField label={t('support.deleteTask.customer')}>
                {customerName
                  ? `${customerName} ${t('support.deleteTask.domainInBrackets', { domainId })}`
                  : t('support.deleteTask.domainOnly', { domainId })}
              </TaskField>
              <TaskField label={t('support.deleteTask.distributedTo')}>
                {distributedTotal ?? 0}
              </TaskField>
              {isScheduled && scheduledFor && (
                <TaskField label={t('support.deleteTask.scheduled')}>
                  {humanDate(scheduledFor)}
                </TaskField>
              )}
              <TaskField label={t('support.deleteTask.ticket')}>
                <CodeValue>{ticket}</CodeValue>
              </TaskField>
              <Column span={[1, 2]}>
                <TaskField label={t('support.deleteTask.reason')}>
                  {reason}
                </TaskField>
              </Column>
            </Grid>
          </Box>
          <TextInput
            data-test-id="confirm-delete-task-input"
            name="confirmDelete"
            autoFocus
            autoComplete="off"
            label={t('support.deleteTask.confirmInputLabel', {
              word: DELETE_CONFIRM_WORD,
            })}
            value={confirmText}
            onChange={({ target: { value } }) => setConfirmText(value)}
          />
          <Stack direction="row" spacing={SIZE.S} justifyContent="flex-end">
            <Button
              data-test-id="cancel-delete-task-button"
              text={t('support.deleteTask.cancel')}
              variant="text"
              onClick={() => onOpenChange(false)}
            />
            <Button
              data-test-id="confirm-delete-task-button"
              text={t('support.deleteTask.confirmPermanentDelete')}
              intent="danger"
              variant="filled"
              disabled={!isConfirmed}
              onClick={onConfirm}
              isLoading={isDeleting}
            />
          </Stack>
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

export default ConfirmDeleteTaskModal;
